import megatron, { get } from '@stencil/megatron';
import {
  TODAY,
  ONWARD,
  RETURN,
  ROUNDTRIP_JOURNEY_TYPE,
  FARE_CALENDAR_API_URL,
  SERVER_FARE_CALENDAR_API_URL,
  ALTERNATE_FLIGHT_OPTIONS_URL,
  DOMESTIC,
  INTERNATIONAL
} from '@stencil/flights-constants';
import { getUniqueId } from '../../../store/flights-reducers/src/utils/common/utils';
import getCommonHeaders from '@stencil/flights-utils/src/get-common-headers';
import {
  isSEMSelector,
  isSEOSelector
} from '../../../store/flights-reducers/src/SRP/redux/selectors';

const daysIntoYear = date => {
  return (
    (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) -
      Date.UTC(date.getFullYear(), 0, 0)) /
    24 /
    60 /
    60 /
    1000
  );
};

const getMonthBefore = date => {
  const startMonth = TODAY.getMonth();
  const selectedMonth = date.getMonth();
  const yearDiff = date.getFullYear() - TODAY.getFullYear();
  return selectedMonth + yearDiff * 12 - startMonth;
};

const getMonthAfter = date => {
  const selectedMonth = date.getMonth();
  return 14 - selectedMonth;
};

const getReturnDayBefore = (start, selected) => {
  const startDay = daysIntoYear(start);
  const selectedDay = daysIntoYear(selected);
  return selectedDay - startDay;
};

const getReturnDayAfter = () => {
  // a year ahead from today
  return 365;
};

const alternateOptionsApi = async paramObj => {
  const params = [];
  for (const key in paramObj) {
    if (paramObj.hasOwnProperty(key)) {
      params.push(key + '=' + paramObj[key]);
    }
  }
  const parameters = params.join('&');
  const url = `${ALTERNATE_FLIGHT_OPTIONS_URL}?${parameters}`;

  var headers = new Headers(getCommonHeaders());
  headers.append('x-pfm', window.flavour || '');
  headers.append('currency', 'inr');
  headers.append('locale', 'en-IN');

  const response = await megatron({
    name: 'alternate flights option',
    type: 'GET',
    headers,
    url: url,
    credentials: 'include',
    trackLatency: { timeout: 2000, data: { serviceApp: 'aerial', apiTimeout: true } }
  });
  if (response) {
    return response;
  }
  return null;
};

export const getAlternateFlightOptionsData = (requestObj, callback) => {
  return (dispatch, getState) => {
    const isSEM = isSEMSelector(getState());
    const isSEO = isSEOSelector(getState());

    const paramObj = {
      ...requestObj,
      deviceid: getUniqueId('no_flights_ab'),
      traffic_source: isSEM ? 'sem' : isSEO ? 'organic' : 'direct'
    };

    alternateOptionsApi(paramObj)
      .then(response => {
        callback(response);
      })
      .catch(err => {
        callback(null, err);
      });
  };
};

const getFareCalendarApi = (mode, data) => {
  const { source, destination, travelType, journeyType, departure, arrival } = data;

  let fareDataUrl = `${FARE_CALENDAR_API_URL}`;
  if (!source || !destination || !departure) {
    return null;
  }
  if (travelType === INTERNATIONAL && journeyType === ROUNDTRIP_JOURNEY_TYPE && mode === ONWARD) {
    return null;
  }
  if (travelType === DOMESTIC || (travelType === INTERNATIONAL && mode === ONWARD)) {
    fareDataUrl += 'o/';
    if (mode === RETURN) {
      fareDataUrl += `${destination.iata}/${source.iata}/${arrival.yyyymmdd}?mb=${
        data.hasOwnProperty('monthBefore') ? data.monthBefore : getMonthBefore(arrival.dateObj)
      }&ma=${data.hasOwnProperty('monthAfter') ? data.monthAfter : getMonthAfter(arrival.dateObj)}`;
    } else {
      fareDataUrl += `${source.iata}/${destination.iata}/${departure.yyyymmdd}?mb=${
        data.hasOwnProperty('monthBefore') ? data.monthBefore : getMonthBefore(departure.dateObj)
      }&ma=${
        data.hasOwnProperty('monthAfter') ? data.monthAfter : getMonthAfter(departure.dateObj)
      }`;
    }
  } else {
    fareDataUrl += `r/${source.iata}/${destination.iata}/${departure.yyyymmdd}/${
      arrival.yyyymmdd
    }?odb=0&oda=0&rdb=${getReturnDayBefore(
      departure.dateObj,
      arrival.dateObj
    )}&rda=${getReturnDayAfter()}`;
  }
  return fareDataUrl;
};

const getFareCalendarApiServer = (mode, data) => {
  const {
    source,
    destination,
    travelType,
    journeyType,
    departure,
    arrival,
    monthBefore = 0,
    monthAfter = 0
  } = data;
  let fareDataUrl = `${SERVER_FARE_CALENDAR_API_URL}`;
  if (!source || !destination || !departure) {
    return null;
  }
  if (travelType === INTERNATIONAL && journeyType === ROUNDTRIP_JOURNEY_TYPE && mode === ONWARD) {
    return null;
  }
  if (travelType === DOMESTIC || (travelType === INTERNATIONAL && mode === ONWARD)) {
    fareDataUrl += 'o/';
    if (mode === RETURN) {
      fareDataUrl += `${destination.iata}/${source.iata}/${arrival.yyyymmdd}?mb=${monthBefore}&ma=${monthAfter}`;
    } else {
      fareDataUrl += `${source.iata}/${destination.iata}/${departure.yyyymmdd}?mb=${monthBefore}&ma=${monthAfter}`;
    }
  }
  return fareDataUrl;
};

export const getFareData = async (mode, data) => {
  const url = getFareCalendarApi(mode, data);

  var headers = new Headers(getCommonHeaders());
  headers.append('x-pfm', window.flavour || '');
  headers.append('currency', 'inr');
  headers.append('locale', 'en-IN');

  const response = await megatron({
    name: 'fare_calendar_api',
    type: 'GET',
    headers,
    url: url,
    credentials: 'omit',
    trackLatency: { timeout: 2000, data: { serviceApp: 'aerial', apiTimeout: true } }
  });
  if (response && response.data) {
    return response;
  }
  return null;
};

export const getFareDataServer = async (mode, data) => {
  const url = getFareCalendarApiServer(mode, data);

  const response = await get(url, {
    name: 'server_fare_calendar_api',
    headers: {
      'x-pfm': data.flavour || '',
      currency: 'inr',
      locale: data.locale || 'en-IN',
      ...getCommonHeaders()
    }
  });

  if (response && response.data) {
    return response.data;
  }
  return null;
};
